import React, { useState } from 'react';
import { AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { META_LABEL } from '../playbooks/editorBits';
import { generateSchemaDraft } from '../../structured-data/structuredDataApi';
import ContractPicker from './ContractPicker';

// "Draft with AI" (doc-74): inline pane above the JSON editor — not a modal,
// so the admin sees the editor fill in beneath it. The server validates the
// draft against the meta-schema before it ever reaches the editor; this pane
// only ships inputs up and the canonical definition down.
export default function DraftWithAiPane({ onDraft, onCancel }) {
  const [description, setDescription] = useState('');
  const [columnHeaders, setColumnHeaders] = useState('');
  const [picked, setPicked] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const generate = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await generateSchemaDraft({
        description: description.trim(),
        columnHeaders: columnHeaders.trim() || undefined,
        contractId: picked?.id || undefined,
      });
      onDraft(res.schema);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    /* stack-md + sde-ai-pane (doc-88): form density — label→input 4px,
       group→group 12px — not the playbook editors' list-row density. */
    <div className="pb-item stack-md sde-ai-pane">
      <div>
        <div style={META_LABEL}>Describe the dataset</div>
        <textarea
          className="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={running}
          placeholder="Describe the dataset in a sentence — e.g. 'Per-corridor payout pricing from partner agreements: corridor, flow, rate, minimums, currency.'"
          style={{ width: '100%', resize: 'vertical', minHeight: 54 }}
          aria-label="Dataset description for the AI draft"
        />
      </div>
      <div>
        <div style={META_LABEL}>Column headers (optional)</div>
        <input
          className="input"
          value={columnHeaders}
          onChange={(e) => setColumnHeaders(e.target.value)}
          disabled={running}
          placeholder="Paste column headers, comma- or tab-separated — e.g. from the spreadsheet this replaces"
          aria-label="Column headers for the AI draft"
        />
      </div>
      <div>
        <div style={META_LABEL}>Sample contract (optional)</div>
        <ContractPicker
          picked={picked}
          onPick={setPicked}
          onClear={() => setPicked(null)}
          ariaLabel="Search contracts to ground the AI draft"
        />
        {!picked && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Grounds the draft in a real document.
          </div>
        )}
      </div>
      {error && (
        <div className="callout red">
          <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
          <div className="callout-body" style={{ fontSize: 12.5 }}>{error}</div>
        </div>
      )}
      {/* flexWrap (doc-273): `.row` doesn't wrap and nothing above it scrolls,
          so at a narrow editor card Cancel — the pane's only way out — was laid
          out past the card edge and reachable by neither pointer nor keyboard. */}
      <div className="row" style={{ gap: 8, marginTop: 'var(--spacing-xs)', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn primary sm"
          onClick={generate}
          disabled={running || !description.trim()}
          aria-busy={running}
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {running ? 'Drafting…' : 'Generate draft'}
        </button>
        <button type="button" className="btn ghost sm" onClick={onCancel} disabled={running}>
          Cancel
        </button>
      </div>
    </div>
  );
}
